import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@ember-data/model';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core-types/symbols';
import type IntlService from 'ember-intl/services/intl';
import type CurrentUserService from 'scaleflow/services/current-user';
import type FeatureAccessService from 'scaleflow/services/feature-access';
import dateSort from 'scaleflow/utils/dateSort';
import nameSort from 'scaleflow/utils/nameSort';
import type AreaModel from './area';
import type CommentModel from './comment';
import type CustomerModel from './customer';
import type DepartmentModel from './department';
import type GroupModel from './group';
import type InitiativeModel from './initiative';
import type PersonModel from './person';
import type ThemeModel from './theme';

export default class IdeaModel extends Model {
  declare [Type]: 'idea';
  @service declare intl: IntlService;
  @service declare currentUser: CurrentUserService;
  @service declare featureAccess: FeatureAccessService;

  @attr('string') declare title: string;
  @attr('', {
    defaultValue: () => {
      return {
        ops: [],
      };
    },
  })
  declare description: unknown;
  @attr('date') declare deadline: Date | undefined;
  @attr('string') declare status: string;
  @attr('string') declare source: string;
  @attr('string') declare revenue_existing: string;
  @attr('string') declare revenue_potential: string;
  @attr declare updated_at: string;
  @attr declare created_at: string;
  @attr declare created_by: string;
  @attr declare updated_by: string;
  @hasMany('department', {
    async: true,
    inverse: null,
  })
  declare departments: AsyncHasMany<DepartmentModel>;

  @hasMany('area', {
    async: true,
    inverse: null,
  })
  declare areas: AsyncHasMany<AreaModel>;

  @belongsTo('person', {
    async: true,
    inverse: 'ideas',
  })
  declare submitter: AsyncBelongsTo<PersonModel>;

  @hasMany('theme', {
    async: true,
    inverse: null,
  })
  declare themes: AsyncHasMany<ThemeModel>;

  @hasMany('customer', {
    async: true,
    inverse: null,
  })
  declare customers: AsyncHasMany<CustomerModel>;

  @belongsTo('group', {
    async: true,
    inverse: 'ideas',
  })
  declare group: AsyncBelongsTo<GroupModel>;

  @hasMany('initiative', {
    async: true,
    inverse: 'ideas',
  })
  declare initiatives: AsyncHasMany<InitiativeModel>;

  @hasMany('comment', {
    async: true,
    inverse: 'idea',
  })
  declare comments: AsyncHasMany<CommentModel>;

  get statusLabel() {
    return this.status
      ? this.intl.t(`idea.status.${this.status.toLowerCase()}`)
      : 'Unknown status';
  }

  get changeset_object() {
    return {
      title: this.title,
      description: this.description,
      deadline: this.deadline,
      status: this.status,
      source: this.source,
      revenue_existing: this.revenue_existing,
      revenue_potential: this.revenue_potential,
      departments: this.departments.content,
      areas: this.areas.content,
      submitter: this.submitter.content,
    };
  }

  mapChangesetToAttributes(changes: { key: string; value: unknown }[]) {
    changes.forEach(({ key, value }) => {
      (this as unknown as Record<string, unknown>)[key] = value;
    });
  }

  get themeIds(): (string | null)[] {
    return (this as IdeaModel).hasMany('themes')?.ids();
  }

  get hasMoreThanOneTheme() {
    const themeCount = this.themes?.content?.length || 0;
    return themeCount > 1 ? themeCount - 1 : 0;
  }

  get departmentIds(): (string | null)[] {
    return (this as IdeaModel).hasMany('departments')?.ids();
  }

  get hasMoreThanOneDepartment() {
    const departmentCount = this.departments?.content?.length || 0;
    return departmentCount > 1 ? departmentCount - 1 : 0;
  }

  get areaIds(): (string | null)[] {
    return (this as IdeaModel).hasMany('areas')?.ids();
  }

  get hasMoreThanOneArea() {
    const areaCount = this.areas?.content?.length || 0;
    return areaCount > 1 ? areaCount - 1 : 0;
  }

  get customerIds(): (string | null)[] {
    return (this as IdeaModel).hasMany('customers')?.ids();
  }

  get hasMoreThanOneCustomer() {
    const customerCount = this.customers?.content?.length || 0;
    return customerCount > 1 ? customerCount - 1 : 0;
  }

  get initiativeIds(): (string | null)[] {
    return (this as IdeaModel).hasMany('initiatives')?.ids();
  }

  get hasMoreThanOneInitiative() {
    const initiativeCount = this.initiatives?.content?.length || 0;
    return initiativeCount > 1 ? initiativeCount - 1 : 0;
  }

  get iAmTheOwner() {
    return this.submitter?.content?.id === this.currentUser.id;
  }

  get canEditIdea() {
    let canEdit = false;
    if (this.isNew) {
      canEdit = true;
      return canEdit;
    }
    const isAdmin = this.featureAccess.isAdmin;
    if (isAdmin) {
      canEdit = true;
      return canEdit;
    }
    if (!isAdmin && this.iAmTheOwner && this.status === 'NEW') {
      canEdit = true;
      return canEdit;
    }
    return canEdit;
  }

  get canEditStatus() {
    let canEdit = false;
    const isAdmin = this.featureAccess.isAdmin;
    if (isAdmin) {
      canEdit = true;
      return canEdit;
    }
    return canEdit;
  }

  get orderedComments() {
    return this.comments?.content
      ?.slice()
      ?.sort((a, b) => dateSort(b.created_at, a.created_at));
  }

  get firstTheme() {
    return [...(this.themes.content ?? [])].sort((a, b) =>
      nameSort(a.title, b.title, 'asc'),
    )[0];
  }

  get firstCustomer() {
    return [...(this.customers.content ?? [])].sort((a, b) =>
      nameSort(a.name, b.name, 'asc'),
    )[0];
  }

  get firstDepartment() {
    return [...(this.departments.content ?? [])].sort((a, b) =>
      nameSort(a.name, b.name, 'asc'),
    )[0];
  }

  get firstArea() {
    return [...(this.areas.content ?? [])].sort((a, b) =>
      nameSort(a.name, b.name, 'asc'),
    )[0];
  }

  get firstInitiative() {
    return [...(this.initiatives.content ?? [])].sort((a, b) =>
      nameSort(a.title, b.title, 'asc'),
    )[0];
  }
}
